import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import B2BCampaignApi, {
  Campaign,
  CampaignCode,
  CampaignFAQItem,
  CreateCampaignPayload,
  GenerateCampaignCodesPayload
} from 'api/b2b/CampaignApi'
import OrbitSession from 'hooks/useCookies'

interface CampaignHowItWorksItem {
  heading: string
  content: string
}

interface CampaignValuePropItem {
  icon: string
  label: string
}

interface CampaignFormState {
  name: string
  description: string
  prefix: string
  landingPageUrl: string
  currency: string
  campaignType: string
  campaignTypeCustom: string
  validityStartAt: string
  validityEndAt: string
  totalCodes: string
  catalogId: string
  maxRedemptions: string
  isNewUserOnly: boolean
  otpVerify: boolean
  manualCodeEntry: boolean
  generateQR: boolean
  uploadQRToS3: boolean
  allowedEmailDomains: string
  metadataTitle: string
  metadataSubtitle: string
  metadataBenefits: string
  metadataCheckoutHeading: string
  metadataFooterHeading: string
  metadataSuccessHeading: string
  metadataSuccessBody: string
  metadataSuccessCTA: string
  howItWorks: CampaignHowItWorksItem[]
  valueProps: CampaignValuePropItem[]
  discountEnabled: boolean
  discountValue: string
  discountType: string
  discountCurrencyValues: string
  discountMaxUsage: string
  promoCode: string
  discountSummary: string
  promoRules: string
  faq: CampaignFAQItem[]
}

interface GenerateCodesState {
  campaignId: string
  campaignName: string
  catalogId: string
  totalCodes: string
  maxRedemptions: string
  generateQR: boolean
  uploadQRToS3: boolean
}

const emptyForm: CampaignFormState = {
  name: '',
  description: '',
  prefix: '',
  landingPageUrl: '',
  currency: 'USD',
  campaignType: 'DATA',
  campaignTypeCustom: '',
  validityStartAt: '',
  validityEndAt: '',
  totalCodes: '0',
  catalogId: '',
  maxRedemptions: '1',
  isNewUserOnly: false,
  otpVerify: false,
  manualCodeEntry: true,
  generateQR: false,
  uploadQRToS3: false,
  allowedEmailDomains: '',
  metadataTitle: '',
  metadataSubtitle: '',
  metadataBenefits: '',
  metadataCheckoutHeading: '',
  metadataFooterHeading: '',
  metadataSuccessHeading: '',
  metadataSuccessBody: '',
  metadataSuccessCTA: '',
  howItWorks: [{ heading: '', content: '' }],
  valueProps: [{ icon: '', label: '' }],
  discountEnabled: false,
  discountValue: '100',
  discountType: 'PERCENTAGE',
  discountCurrencyValues: '',
  discountMaxUsage: '1',
  promoCode: '',
  discountSummary: '',
  promoRules: '',
  faq: [{ question: '', answer: '' }]
}

const metadataFields = [
  'title',
  'subtitle',
  'benefits',
  'checkoutHeading',
  'howitworks',
  'valueProps',
  'SuccessScreen',
  'footerHeading',
  'faqs',
  'otpVerify',
  'manualCodeEntry'
]

const campaignTypeOptions = ['DATA', 'DATA_AND_DISCOUNT', 'DISCOUNT']

const getCampaignType = (form: CampaignFormState) =>
  form.campaignTypeCustom.trim() || form.campaignType.trim() || 'DATA'

const toUnixMilli = (dateValue: string) => new Date(dateValue).getTime()

const formatUnixMilli = (value?: number) => {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

const parseCsv = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const parseCurrencyValues = (value: string) => {
  const entries = parseCsv(value)
  if (entries.length === 0) return undefined

  return entries.reduce<Record<string, string>>((acc, entry) => {
    const [currency, amount] = entry.split('=').map((part) => part.trim())
    if (currency && amount) {
      acc[currency.toUpperCase()] = amount
    }
    return acc
  }, {})
}

const parseNumberCsv = (value: string) =>
  parseCsv(value)
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item))

const addMetadataString = (
  metadata: Record<string, unknown>,
  key: string,
  value: string
) => {
  const trimmed = value.trim()

  if (trimmed) {
    metadata[key] = trimmed
  }
}

const buildCampaignMetadata = (
  form: CampaignFormState,
  faq: CampaignFAQItem[]
): { otpVerify: boolean; manualCodeEntry: boolean } & Record<
  string,
  unknown
> => {
  const metadata: { otpVerify: boolean; manualCodeEntry: boolean } & Record<
    string,
    unknown
  > = {
    otpVerify: form.otpVerify,
    manualCodeEntry: form.manualCodeEntry
  }

  addMetadataString(metadata, 'title', form.metadataTitle)
  addMetadataString(metadata, 'subtitle', form.metadataSubtitle)
  addMetadataString(metadata, 'benefits', form.metadataBenefits)
  addMetadataString(metadata, 'checkoutHeading', form.metadataCheckoutHeading)
  addMetadataString(metadata, 'footerHeading', form.metadataFooterHeading)

  const howitworks = form.howItWorks
    .map((item) => ({
      heading: item.heading.trim(),
      content: item.content.trim()
    }))
    .filter((item) => item.heading || item.content)

  if (howitworks.length > 0) {
    metadata.howitworks = howitworks
  }

  const valueProps = form.valueProps
    .map((item) => ({
      icon: item.icon.trim(),
      label: item.label.trim()
    }))
    .filter((item) => item.icon || item.label)

  if (valueProps.length > 0) {
    metadata.valueProps = valueProps
  }

  if (faq.length > 0) {
    metadata.faqs = faq
  }

  if (
    form.metadataSuccessHeading.trim() ||
    form.metadataSuccessBody.trim() ||
    form.metadataSuccessCTA.trim()
  ) {
    metadata.SuccessScreen = {
      heading: form.metadataSuccessHeading.trim(),
      body: form.metadataSuccessBody.trim(),
      CTA: form.metadataSuccessCTA.trim()
    }
  }

  return metadata
}

const getApiMessage = (error: unknown, fallback: string) => {
  const anyError = error as {
    response?: { data?: { message?: string; error?: string } }
    message?: string
  }

  return (
    anyError?.response?.data?.message ||
    anyError?.response?.data?.error ||
    anyError?.message ||
    fallback
  )
}

const buildCreatePayload = (
  form: CampaignFormState
): CreateCampaignPayload | null => {
  const name = form.name.trim()
  const prefix = form.prefix.trim()
  const validityStartAt = toUnixMilli(form.validityStartAt)
  const validityEndAt = toUnixMilli(form.validityEndAt)
  const totalCodes = Number(form.totalCodes || 0)
  const maxRedemptions = Number(form.maxRedemptions || 1)
  const discountValue = Number(form.discountValue || 0)
  const discountMaxUsage = Number(form.discountMaxUsage || 0)
  const faq = form.faq
    .map((item) => ({
      question: item.question.trim(),
      answer: item.answer.trim()
    }))
    .filter((item) => item.question && item.answer)

  if (!name) {
    toast.error('Campaign name is required')
    return null
  }

  if (!/^[A-Za-z0-9_-]{2,20}$/.test(prefix)) {
    toast.error(
      'Prefix must be 2-20 characters and use letters, numbers, _ or -'
    )
    return null
  }

  if (!form.validityStartAt || !form.validityEndAt) {
    toast.error('Validity start and end are required')
    return null
  }

  if (validityStartAt >= validityEndAt) {
    toast.error('Validity start must be before validity end')
    return null
  }

  if (totalCodes < 0 || !Number.isFinite(totalCodes)) {
    toast.error('Total codes must be zero or greater')
    return null
  }

  if (totalCodes > 0 && !form.catalogId.trim()) {
    toast.error('Catalog ID is required when generating codes')
    return null
  }

  if (maxRedemptions <= 0 || !Number.isFinite(maxRedemptions)) {
    toast.error('Max redemptions must be greater than zero')
    return null
  }

  if (form.discountEnabled && discountValue <= 0) {
    toast.error('Discount value must be greater than zero')
    return null
  }

  const allowedEmailDomains = parseCsv(form.allowedEmailDomains)
  const discountCurrencyValues = parseCurrencyValues(
    form.discountCurrencyValues
  )
  const metadata = buildCampaignMetadata(form, faq)

  const payload: CreateCampaignPayload = {
    name,
    description: form.description.trim(),
    prefix,
    landingPageUrl: form.landingPageUrl.trim(),
    currency: form.currency.trim().toUpperCase() || 'USD',
    totalCodes,
    validityStartAt,
    validityEndAt,
    metadata,
    campaignType: getCampaignType(form),
    isNewUserOnly: form.isNewUserOnly,
    catalogId: form.catalogId.trim() || undefined,
    maxRedemptions,
    generateQR: form.generateQR,
    uploadQRToS3: form.uploadQRToS3
  }

  if (allowedEmailDomains.length > 0) {
    payload.ruleMetadata = { allowedEmailDomains }
  }

  if (faq.length > 0) {
    payload.faq = faq
  }

  if (form.discountEnabled) {
    payload.discount = {
      discountValue,
      discountType: form.discountType.trim() || 'PERCENTAGE',
      discountCurrencyValues,
      maxUsage: discountMaxUsage > 0 ? discountMaxUsage : 1,
      promoCode: form.promoCode.trim() || prefix,
      summary: form.discountSummary.trim(),
      promoRules: parseNumberCsv(form.promoRules)
    }
  }

  return payload
}

function B2BCampaignManagement() {
  const [tab, setTab] = useState(0)
  const [form, setForm] = useState<CampaignFormState>(emptyForm)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  )
  const [codesDialogOpen, setCodesDialogOpen] = useState(false)
  const [codes, setCodes] = useState<CampaignCode[]>([])
  const [codeTotal, setCodeTotal] = useState(0)
  const [generateDialog, setGenerateDialog] =
    useState<GenerateCodesState | null>(null)

  const token = OrbitSession.get('faberToken')
  const queryClient = useQueryClient()

  const campaignQuery = useQuery(
    ['b2b-campaigns'],
    () => B2BCampaignApi.listCampaigns(token, { limit: 100 }),
    {
      enabled: Boolean(token),
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to load campaigns'))
      }
    }
  )

  const campaigns = useMemo(
    () => campaignQuery.data?.data?.campaigns || [],
    [campaignQuery.data]
  )

  const createCampaignMutation = useMutation(
    (payload: CreateCampaignPayload) =>
      B2BCampaignApi.createCampaign(token, payload),
    {
      onSuccess: () => {
        toast.success('Campaign created successfully')
        setForm(emptyForm)
        setTab(0)
        queryClient.invalidateQueries(['b2b-campaigns'])
      },
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to create campaign'))
      }
    }
  )

  const listCodesMutation = useMutation(
    (campaignId: string) =>
      B2BCampaignApi.listCodes(token, campaignId, { limit: 500 }),
    {
      onSuccess: (response) => {
        setCodes(response.data?.codes || [])
        setCodeTotal(response.data?.total || 0)
        setCodesDialogOpen(true)
      },
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to load campaign codes'))
      }
    }
  )

  const generateCodesMutation = useMutation(
    ({
      campaignId,
      payload
    }: {
      campaignId: string
      payload: GenerateCampaignCodesPayload
    }) => B2BCampaignApi.generateCodes(token, campaignId, payload),
    {
      onSuccess: () => {
        toast.success('Codes generated successfully')
        setGenerateDialog(null)
        queryClient.invalidateQueries(['b2b-campaigns'])
      },
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to generate campaign codes'))
      }
    }
  )

  const exportCSVMutation = useMutation(
    (campaignId: string) => B2BCampaignApi.exportCodesCSV(token, campaignId),
    {
      onSuccess: () => {
        toast.success('CSV exported')
      },
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to export codes CSV'))
      }
    }
  )

  const downloadQRMutation = useMutation(
    (campaignId: string) => B2BCampaignApi.downloadQRCodes(token, campaignId),
    {
      onSuccess: () => {
        toast.success('QR ZIP downloaded')
      },
      onError: (error) => {
        toast.error(getApiMessage(error, 'Failed to download QR ZIP'))
      }
    }
  )

  const updateForm = <K extends keyof CampaignFormState>(
    key: K,
    value: CampaignFormState[K]
  ) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const updateFaq = (
    index: number,
    key: keyof CampaignFAQItem,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      faq: current.faq.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      )
    }))
  }

  const addFaq = () => {
    setForm((current) => ({
      ...current,
      faq: [...current.faq, { question: '', answer: '' }]
    }))
  }

  const removeFaq = (index: number) => {
    setForm((current) => ({
      ...current,
      faq:
        current.faq.length === 1
          ? [{ question: '', answer: '' }]
          : current.faq.filter((_item, itemIndex) => itemIndex !== index)
    }))
  }

  const updateHowItWorks = (
    index: number,
    key: keyof CampaignHowItWorksItem,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      howItWorks: current.howItWorks.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      )
    }))
  }

  const addHowItWorks = () => {
    setForm((current) => ({
      ...current,
      howItWorks: [...current.howItWorks, { heading: '', content: '' }]
    }))
  }

  const removeHowItWorks = (index: number) => {
    setForm((current) => ({
      ...current,
      howItWorks:
        current.howItWorks.length === 1
          ? [{ heading: '', content: '' }]
          : current.howItWorks.filter((_item, itemIndex) => itemIndex !== index)
    }))
  }

  const updateValueProp = (
    index: number,
    key: keyof CampaignValuePropItem,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      valueProps: current.valueProps.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      )
    }))
  }

  const addValueProp = () => {
    setForm((current) => ({
      ...current,
      valueProps: [...current.valueProps, { icon: '', label: '' }]
    }))
  }

  const removeValueProp = (index: number) => {
    setForm((current) => ({
      ...current,
      valueProps:
        current.valueProps.length === 1
          ? [{ icon: '', label: '' }]
          : current.valueProps.filter((_item, itemIndex) => itemIndex !== index)
    }))
  }

  const submitCampaign = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const payload = buildCreatePayload(form)
    if (!payload) return

    createCampaignMutation.mutate(payload)
  }

  const submitGenerateCodes = () => {
    if (!generateDialog) return

    const totalCodes = Number(generateDialog.totalCodes || 0)
    const maxRedemptions = Number(generateDialog.maxRedemptions || 1)

    if (!generateDialog.catalogId.trim()) {
      toast.error('Catalog ID is required')
      return
    }

    if (totalCodes <= 0 || !Number.isFinite(totalCodes)) {
      toast.error('Total codes must be greater than zero')
      return
    }

    generateCodesMutation.mutate({
      campaignId: generateDialog.campaignId,
      payload: {
        catalogId: generateDialog.catalogId.trim(),
        totalCodes,
        maxRedemptions: maxRedemptions > 0 ? maxRedemptions : 1,
        generateQR: generateDialog.generateQR,
        uploadQRToS3: generateDialog.uploadQRToS3
      }
    })
  }

  const payloadPreview = useMemo(() => {
    const payload = buildCreatePayloadPreview(form)
    return JSON.stringify(payload, null, 2)
  }, [form])

  return (
    <Box>
      <Paper
        sx={{
          background: '#000869',
          padding: '16px',
          borderRadius: '8px',
          mb: 2
        }}
      >
        <Typography variant='h4' color='#fff'>
          B2B Campaign Management
        </Typography>
      </Paper>

      <Paper sx={{ borderRadius: '8px' }}>
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          sx={{ borderBottom: '1px solid #e5e7eb', px: 2 }}
        >
          <Tab label='Campaigns' />
          <Tab label='Create Campaign' />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 2 }}
            >
              <Typography variant='h6'>Campaigns</Typography>
              <Button
                variant='contained'
                onClick={() => setTab(1)}
                disabled={createCampaignMutation.isLoading}
              >
                Create Campaign
              </Button>
            </Stack>

            {campaignQuery.isLoading ? (
              <Stack alignItems='center' sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Validity</TableCell>
                      <TableCell>Codes</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align='right'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {campaign.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {campaign.campaignType || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{campaign.prefix}</TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {formatUnixMilli(campaign.validityStartAt)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatUnixMilli(campaign.validityEndAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {campaign.totalCodes || 0} total
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {campaign.totalRedeemed || 0} redeemed
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={campaign.isActive ? 'Active' : 'Inactive'}
                            color={campaign.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Stack
                            direction='row'
                            spacing={1}
                            justifyContent='flex-end'
                          >
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() => setSelectedCampaign(campaign)}
                            >
                              View
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() =>
                                listCodesMutation.mutate(campaign.id)
                              }
                              disabled={listCodesMutation.isLoading}
                            >
                              Codes
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() =>
                                setGenerateDialog({
                                  campaignId: campaign.id,
                                  campaignName: campaign.name,
                                  catalogId: '',
                                  totalCodes: '100',
                                  maxRedemptions: '1',
                                  generateQR: false,
                                  uploadQRToS3: false
                                })
                              }
                            >
                              Generate
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() =>
                                downloadQRMutation.mutate(campaign.id)
                              }
                              disabled={downloadQRMutation.isLoading}
                            >
                              QR ZIP
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                    {campaigns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography
                            align='center'
                            color='text.secondary'
                            sx={{ py: 4 }}
                          >
                            No campaigns found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box component='form' onSubmit={submitCampaign} sx={{ p: 2 }}>
            <Alert severity='info' sx={{ mb: 2 }}>
              Campaigns are created live in B2B. Review prefix, validity,
              catalog, discount, and code volume before submitting.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    Campaign Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Campaign Name'
                        value={form.name}
                        onChange={(event) =>
                          updateForm('name', event.target.value)
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label='Prefix'
                        value={form.prefix}
                        onChange={(event) =>
                          updateForm('prefix', event.target.value)
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label='Currency'
                        value={form.currency}
                        onChange={(event) =>
                          updateForm(
                            'currency',
                            event.target.value.toUpperCase()
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Description'
                        value={form.description}
                        onChange={(event) =>
                          updateForm('description', event.target.value)
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Landing Page URL'
                        value={form.landingPageUrl}
                        onChange={(event) =>
                          updateForm('landingPageUrl', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        label='Campaign Type'
                        value={form.campaignType}
                        onChange={(event) =>
                          updateForm('campaignType', event.target.value)
                        }
                        fullWidth
                      >
                        {campaignTypeOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label='Custom Campaign Type'
                        value={form.campaignTypeCustom}
                        onChange={(event) =>
                          updateForm('campaignTypeCustom', event.target.value)
                        }
                        helperText='Overrides selected type when filled'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Validity Start'
                        type='datetime-local'
                        value={form.validityStartAt}
                        onChange={(event) =>
                          updateForm('validityStartAt', event.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Validity End'
                        type='datetime-local'
                        value={form.validityEndAt}
                        onChange={(event) =>
                          updateForm('validityEndAt', event.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    Redemption And Codes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label='Total Codes'
                        type='number'
                        value={form.totalCodes}
                        onChange={(event) =>
                          updateForm('totalCodes', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label='Catalog ID'
                        value={form.catalogId}
                        onChange={(event) =>
                          updateForm('catalogId', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label='Max Redemptions'
                        type='number'
                        value={form.maxRedemptions}
                        onChange={(event) =>
                          updateForm('maxRedemptions', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction='row' spacing={3} flexWrap='wrap'>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.isNewUserOnly}
                              onChange={(event) =>
                                updateForm(
                                  'isNewUserOnly',
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label='New user only'
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.otpVerify}
                              onChange={(event) =>
                                updateForm('otpVerify', event.target.checked)
                              }
                            />
                          }
                          label='OTP verification'
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.manualCodeEntry}
                              onChange={(event) =>
                                updateForm(
                                  'manualCodeEntry',
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label='Manual code entry'
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.generateQR}
                              onChange={(event) =>
                                updateForm('generateQR', event.target.checked)
                              }
                            />
                          }
                          label='Generate QR ZIP'
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.uploadQRToS3}
                              onChange={(event) =>
                                updateForm('uploadQRToS3', event.target.checked)
                              }
                            />
                          }
                          label='Upload QR to S3'
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Allowed Email Domains'
                        value={form.allowedEmailDomains}
                        onChange={(event) =>
                          updateForm('allowedEmailDomains', event.target.value)
                        }
                        helperText='Comma separated, for example hsbc.com,jp.hsbc.com'
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    Metadata
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={1}
                    flexWrap='wrap'
                    sx={{ mb: 2 }}
                  >
                    {metadataFields.map((field) => (
                      <Chip key={field} label={field} size='small' />
                    ))}
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Title'
                        value={form.metadataTitle}
                        onChange={(event) =>
                          updateForm('metadataTitle', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Subtitle'
                        value={form.metadataSubtitle}
                        onChange={(event) =>
                          updateForm('metadataSubtitle', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Benefits'
                        value={form.metadataBenefits}
                        onChange={(event) =>
                          updateForm('metadataBenefits', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Checkout Heading'
                        value={form.metadataCheckoutHeading}
                        onChange={(event) =>
                          updateForm(
                            'metadataCheckoutHeading',
                            event.target.value
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Footer Heading'
                        value={form.metadataFooterHeading}
                        onChange={(event) =>
                          updateForm(
                            'metadataFooterHeading',
                            event.target.value
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 2 }}
                  >
                    <Typography variant='subtitle1'>How It Works</Typography>
                    <Button
                      type='button'
                      variant='outlined'
                      onClick={addHowItWorks}
                    >
                      Add Step
                    </Button>
                  </Stack>
                  <Stack spacing={2}>
                    {form.howItWorks.map((item, index) => (
                      <Paper key={index} variant='outlined' sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label='Heading'
                              value={item.heading}
                              onChange={(event) =>
                                updateHowItWorks(
                                  index,
                                  'heading',
                                  event.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label='Content'
                              value={item.content}
                              onChange={(event) =>
                                updateHowItWorks(
                                  index,
                                  'content',
                                  event.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <Button
                              type='button'
                              color='error'
                              onClick={() => removeHowItWorks(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>

                  <Divider sx={{ my: 2 }} />
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 2 }}
                  >
                    <Typography variant='subtitle1'>Value Props</Typography>
                    <Button
                      type='button'
                      variant='outlined'
                      onClick={addValueProp}
                    >
                      Add Value Prop
                    </Button>
                  </Stack>
                  <Stack spacing={2}>
                    {form.valueProps.map((item, index) => (
                      <Paper key={index} variant='outlined' sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label='Icon URL'
                              value={item.icon}
                              onChange={(event) =>
                                updateValueProp(
                                  index,
                                  'icon',
                                  event.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label='Label'
                              value={item.label}
                              onChange={(event) =>
                                updateValueProp(
                                  index,
                                  'label',
                                  event.target.value
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <Button
                              type='button'
                              color='error'
                              onClick={() => removeValueProp(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant='subtitle1' sx={{ mb: 2 }}>
                    Success Screen
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label='Success Heading'
                        value={form.metadataSuccessHeading}
                        onChange={(event) =>
                          updateForm(
                            'metadataSuccessHeading',
                            event.target.value
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Success Body'
                        value={form.metadataSuccessBody}
                        onChange={(event) =>
                          updateForm('metadataSuccessBody', event.target.value)
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Success CTA'
                        value={form.metadataSuccessCTA}
                        onChange={(event) =>
                          updateForm('metadataSuccessCTA', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 2 }}
                  >
                    <Typography variant='h6'>Discount</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.discountEnabled}
                          onChange={(event) =>
                            updateForm('discountEnabled', event.target.checked)
                          }
                        />
                      }
                      label='Enable discount'
                    />
                  </Stack>

                  {form.discountEnabled && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label='Discount Value'
                          type='number'
                          value={form.discountValue}
                          onChange={(event) =>
                            updateForm('discountValue', event.target.value)
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          label='Discount Type'
                          value={form.discountType}
                          onChange={(event) =>
                            updateForm('discountType', event.target.value)
                          }
                          fullWidth
                        >
                          <MenuItem value='PERCENTAGE'>PERCENTAGE</MenuItem>
                          <MenuItem value='FIXED'>FIXED</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label='Max Usage'
                          type='number'
                          value={form.discountMaxUsage}
                          onChange={(event) =>
                            updateForm('discountMaxUsage', event.target.value)
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label='Promo Code'
                          value={form.promoCode}
                          onChange={(event) =>
                            updateForm(
                              'promoCode',
                              event.target.value.toUpperCase()
                            )
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label='Summary'
                          value={form.discountSummary}
                          onChange={(event) =>
                            updateForm('discountSummary', event.target.value)
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label='Currency Values'
                          value={form.discountCurrencyValues}
                          onChange={(event) =>
                            updateForm(
                              'discountCurrencyValues',
                              event.target.value
                            )
                          }
                          helperText='Comma separated key/value pairs, for example USD=100,SGD=100'
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label='Promo Rules'
                          value={form.promoRules}
                          onChange={(event) =>
                            updateForm('promoRules', event.target.value)
                          }
                          helperText='Comma separated rule IDs'
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  )}
                </Paper>

                <Paper variant='outlined' sx={{ p: 2 }}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 2 }}
                  >
                    <Typography variant='h6'>FAQ</Typography>
                    <Button type='button' variant='outlined' onClick={addFaq}>
                      Add FAQ
                    </Button>
                  </Stack>

                  <Stack spacing={2}>
                    {form.faq.map((item, index) => (
                      <Paper key={index} variant='outlined' sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label='Question'
                              value={item.question}
                              onChange={(event) =>
                                updateFaq(index, 'question', event.target.value)
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label='Answer'
                              value={item.answer}
                              onChange={(event) =>
                                updateFaq(index, 'answer', event.target.value)
                              }
                              fullWidth
                              multiline
                              minRows={1}
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <Button
                              type='button'
                              color='error'
                              onClick={() => removeFaq(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    position: 'sticky',
                    top: 16
                  }}
                >
                  <Typography variant='h6' sx={{ mb: 1 }}>
                    Review
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Typography variant='body2'>
                      <strong>Name:</strong> {form.name || '-'}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>Prefix:</strong> {form.prefix || '-'}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>Codes:</strong> {form.totalCodes || '0'}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>Discount:</strong>{' '}
                      {form.discountEnabled
                        ? `${form.discountValue} ${form.discountType}`
                        : 'Disabled'}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>FAQ:</strong>{' '}
                      {
                        form.faq.filter(
                          (item) => item.question.trim() && item.answer.trim()
                        ).length
                      }{' '}
                      entries
                    </Typography>
                  </Stack>

                  <TextField
                    label='Payload Preview'
                    value={payloadPreview}
                    fullWidth
                    multiline
                    minRows={12}
                    InputProps={{ readOnly: true }}
                  />

                  <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={createCampaignMutation.isLoading}
                      fullWidth
                    >
                      {createCampaignMutation.isLoading
                        ? 'Creating...'
                        : 'Create Campaign'}
                    </Button>
                    <Button
                      type='button'
                      variant='outlined'
                      onClick={() => setForm(emptyForm)}
                      disabled={createCampaignMutation.isLoading}
                    >
                      Reset
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Dialog
        open={Boolean(selectedCampaign)}
        onClose={() => setSelectedCampaign(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selectedCampaign?.name || 'Campaign Details'}
        </DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>Campaign ID</Typography>
                <Typography>{selectedCampaign.id}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>Landing Page</Typography>
                <Typography>
                  {selectedCampaign.landingPageUrl || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>Currency</Typography>
                <Typography>{selectedCampaign.currency || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>New User Only</Typography>
                <Typography>
                  {selectedCampaign.isNewUserOnly ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>Created By</Typography>
                <Typography>{selectedCampaign.createdBy || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant='caption'>Created At</Typography>
                <Typography>{selectedCampaign.createdAt || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCampaign(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={codesDialogOpen}
        onClose={() => setCodesDialogOpen(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>Campaign Codes</DialogTitle>
        <DialogContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Typography color='text.secondary'>
              Showing {codes.length} of {codeTotal} codes
            </Typography>
            <Button
              variant='outlined'
              onClick={() =>
                codes.length > 0 &&
                exportCSVMutation.mutate(codes[0].campaignId)
              }
              disabled={codes.length === 0 || exportCSVMutation.isLoading}
            >
              {exportCSVMutation.isLoading ? 'Exporting...' : 'Export All CSV'}
            </Button>
          </Stack>
          <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Catalog ID</TableCell>
                  <TableCell>Redemptions</TableCell>
                  <TableCell>Redeemed Emails</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell>{code.code}</TableCell>
                    <TableCell>{code.status}</TableCell>
                    <TableCell>{code.catalogId}</TableCell>
                    <TableCell>
                      {code.currentRedemptions}/{code.maxRedemptions}
                    </TableCell>
                    <TableCell>
                      {(code.redeemedByEmails || []).join(', ') || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(generateDialog)}
        onClose={() => setGenerateDialog(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Generate Campaign Codes</DialogTitle>
        <DialogContent>
          {generateDialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography color='text.secondary'>
                {generateDialog.campaignName}
              </Typography>
              <TextField
                label='Catalog ID'
                value={generateDialog.catalogId}
                onChange={(event) =>
                  setGenerateDialog({
                    ...generateDialog,
                    catalogId: event.target.value
                  })
                }
                fullWidth
              />
              <TextField
                label='Total Codes'
                type='number'
                value={generateDialog.totalCodes}
                onChange={(event) =>
                  setGenerateDialog({
                    ...generateDialog,
                    totalCodes: event.target.value
                  })
                }
                fullWidth
              />
              <TextField
                label='Max Redemptions'
                type='number'
                value={generateDialog.maxRedemptions}
                onChange={(event) =>
                  setGenerateDialog({
                    ...generateDialog,
                    maxRedemptions: event.target.value
                  })
                }
                fullWidth
              />
              <Stack direction='row' spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={generateDialog.generateQR}
                      onChange={(event) =>
                        setGenerateDialog({
                          ...generateDialog,
                          generateQR: event.target.checked
                        })
                      }
                    />
                  }
                  label='Generate QR ZIP'
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={generateDialog.uploadQRToS3}
                      onChange={(event) =>
                        setGenerateDialog({
                          ...generateDialog,
                          uploadQRToS3: event.target.checked
                        })
                      }
                    />
                  }
                  label='Upload QR to S3'
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(null)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={submitGenerateCodes}
            disabled={generateCodesMutation.isLoading}
          >
            {generateCodesMutation.isLoading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const buildCreatePayloadPreview = (form: CampaignFormState) => {
  const totalCodes = Number(form.totalCodes || 0)
  const maxRedemptions = Number(form.maxRedemptions || 1)
  const faq = form.faq
    .map((item) => ({
      question: item.question.trim(),
      answer: item.answer.trim()
    }))
    .filter((item) => item.question && item.answer)
  const allowedEmailDomains = parseCsv(form.allowedEmailDomains)
  const metadata = buildCampaignMetadata(form, faq)

  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    description: form.description.trim(),
    prefix: form.prefix.trim(),
    landingPageUrl: form.landingPageUrl.trim(),
    currency: form.currency.trim().toUpperCase() || 'USD',
    totalCodes,
    validityStartAt: form.validityStartAt
      ? toUnixMilli(form.validityStartAt)
      : null,
    validityEndAt: form.validityEndAt ? toUnixMilli(form.validityEndAt) : null,
    metadata,
    campaignType: getCampaignType(form),
    isNewUserOnly: form.isNewUserOnly,
    catalogId: form.catalogId.trim() || undefined,
    maxRedemptions,
    generateQR: form.generateQR,
    uploadQRToS3: form.uploadQRToS3
  }

  if (allowedEmailDomains.length > 0) {
    payload.ruleMetadata = { allowedEmailDomains }
  }

  if (faq.length > 0) {
    payload.faq = faq
  }

  if (form.discountEnabled) {
    payload.discount = {
      discountValue: Number(form.discountValue || 0),
      discountType: form.discountType.trim() || 'PERCENTAGE',
      discountCurrencyValues: parseCurrencyValues(form.discountCurrencyValues),
      maxUsage: Number(form.discountMaxUsage || 1),
      promoCode: form.promoCode.trim() || form.prefix.trim(),
      summary: form.discountSummary.trim(),
      promoRules: parseNumberCsv(form.promoRules)
    }
  }

  return payload
}

export default B2BCampaignManagement
